import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Workspace } from '@/types/api';

/** Fetch the workspace for an organization. The backend returns a single
 *  Workspace per org (1:1) — but historically callers in this codebase
 *  used `workspaces.data?.[0]` expecting an array. We expose a single
 *  object now so the call sites are explicit, and provide a `useWorkspaces`
 *  alias that returns `{ data: [workspace] | undefined }` for legacy callers
 *  that haven't migrated yet. */
export function useWorkspace(orgId: string | null | undefined) {
  return useQuery({
    enabled: !!orgId,
    queryKey: ['workspace', orgId],
    queryFn: async () => (await api.get<Workspace>(`/workspaces/${orgId}`)).data,
  });
}

/** Fetch workspace by its own ID (not org ID). Used when only workspace_id is known. */
export function useWorkspaceByWorkspaceId(workspaceId: string | null | undefined) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ['workspace-by-id', workspaceId],
    queryFn: async () => (await api.get<Workspace>(`/workspaces/by-id/${workspaceId}`)).data,
  });
}

/** Legacy shape: returns the single workspace wrapped in an array.
 *  Existing call sites use `workspaces.data?.[0]?.id` so this keeps them
 *  working without forcing a sweep right now. New code should call
 *  `useWorkspace()` and use `data?.id` directly. */
export function useWorkspaces(orgId: string | null | undefined) {
  const q = useWorkspace(orgId);
  return {
    ...q,
    data: q.data ? [q.data] : undefined,
  };
}
